import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  clearImpersonationSession,
  loadImpersonationSession,
} from "./supportModeSession";
import { buildSupportModeReturnUrl } from "./saUiOrigin";
import "./SupportModeBanner.css";

/**
 * Return the SA operator to the Super Admin app of THIS stage
 * (REACT_APP_SA_UI_ORIGIN from .env-cmdrc). Fail closed: if the stage is not
 * configured we surface the error instead of guessing another environment.
 */
function returnToSuperAdmin() {
  window.location.assign(buildSupportModeReturnUrl());
}

function getApiBase() {
  return (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_ADMIN_API_URL ||
    "https://admin-staging-api-v2.krosskast.com"
  ).replace(/\/$/, "");
}

function shortActorLabel(session) {
  const raw = session.userDisplayName || "client user";
  // Prefer business name when the raw subject looks like an Auth0 user id.
  if (/^auth0\|/i.test(String(raw)) && session.businessName) {
    return session.businessName;
  }
  return raw;
}

export default function SupportModeBanner() {
  const [session, setSession] = useState(() => loadImpersonationSession());
  const [ending, setEnding] = useState(false);
  const [showDisposition, setShowDisposition] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [endError, setEndError] = useState(null);
  const ref = useRef(null);

  // Poll session expiry without putting `session` in deps — loadImpersonationSession
  // JSON.parses a new object every tick; depending on it would reset the interval
  // and force banner layout effects on every poll.
  useEffect(() => {
    const id = setInterval(() => {
      const next = loadImpersonationSession();
      setSession((prev) => {
        if (!prev && !next) return prev;
        if (
          prev &&
          next &&
          prev.ticketId === next.ticketId &&
          prev.expiresAt === next.expiresAt &&
          prev.token === next.token
        ) {
          return prev;
        }
        if (!next && prev) {
          try {
            returnToSuperAdmin();
          } catch (err) {
            console.error(err);
          }
        }
        return next;
      });
    }, 15000);
    return () => clearInterval(id);
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      document.documentElement.style.setProperty("--kk-support-banner-height", "0px");
      document.documentElement.classList.remove("kk-support-mode");
      return undefined;
    }
    const publish = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        "--kk-support-banner-height",
        `${h}px`,
      );
      document.documentElement.classList.add("kk-support-mode");
    };
    publish();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    observer?.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.setProperty("--kk-support-banner-height", "0px");
      document.documentElement.classList.remove("kk-support-mode");
    };
  }, [session]);

  if (!session) return null;

  const endsAt = session.expiresAt
    ? new Date(session.expiresAt).toLocaleString()
    : "unknown";

  const openDispositionModal = () => {
    setEndError(null);
    setDisposition("");
    setResolutionNote("");
    setShowDisposition(true);
  };

  const closeDispositionModal = () => {
    if (ending) return;
    setShowDisposition(false);
    setEndError(null);
  };

  const endSession = async () => {
    if (!disposition) {
      setEndError("Select whether the issue was resolved.");
      return;
    }
    setEnding(true);
    setEndError(null);
    try {
      const body = {
        caseDisposition: disposition,
      };
      if (
        disposition === "resolved" &&
        resolutionNote.trim()
      ) {
        body.resolutionNote = resolutionNote.trim();
      }
      const res = await fetch(
        `${getApiBase()}/sa/impersonation-tickets/${encodeURIComponent(session.ticketId)}/end`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.token}`,
          },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let message = `Failed to end Support Mode (${res.status})`;
        try {
          const json = await res.json();
          message = json?.message || json?.error || message;
        } catch {
          /* ignore */
        }
        setEndError(message);
        setEnding(false);
        return;
      }
      // Resolve the return origin BEFORE clearing the session so a missing
      // stage config leaves the operator in a recoverable state.
      const returnUrl = buildSupportModeReturnUrl();
      clearImpersonationSession();
      window.location.assign(returnUrl);
    } catch (err) {
      console.warn("end impersonation failed", err);
      setEndError(err?.message || "Failed to end Support Mode");
      setEnding(false);
    }
  };

  const who = shortActorLabel(session);

  return (
    <>
      <div
        ref={ref}
        className="kk-support-mode-banner"
        role="status"
        aria-live="polite"
      >
        <span className="kk-support-mode-banner__text">
          Impersonating {who}
          {session.saDisplayName ? ` — SA: ${session.saDisplayName}` : ""}
          {session.supportTicketId
            ? ` · Case ID: ${session.supportTicketId}`
            : ""}
          {" · "}Ends {endsAt}
        </span>
        <button
          type="button"
          className="kk-support-mode-banner__end"
          onClick={openDispositionModal}
          disabled={ending}
        >
          {ending ? "Ending…" : "End impersonation"}
        </button>
      </div>

      {showDisposition && (
        <div
          className="kk-support-disposition-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kk-support-disposition-title"
        >
          <div className="kk-support-disposition-modal">
            <h2 id="kk-support-disposition-title">Close this Support Case?</h2>
            {session.supportTicketId ? (
              <p className="kk-support-disposition-case">
                Case ID: <strong>{session.supportTicketId}</strong>
              </p>
            ) : null}
            {endError ? (
              <p className="kk-support-disposition-error">{endError}</p>
            ) : null}
            <label className="kk-support-disposition-option">
              <input
                type="radio"
                name="caseDisposition"
                value="resolved"
                checked={disposition === "resolved"}
                onChange={() => setDisposition("resolved")}
                disabled={ending}
              />
              <span className="kk-support-disposition-option__text">
                Close Support Case
              </span>
            </label>
            <label className="kk-support-disposition-option">
              <input
                type="radio"
                name="caseDisposition"
                value="further_support_needed"
                checked={disposition === "further_support_needed"}
                onChange={() => setDisposition("further_support_needed")}
                disabled={ending}
              />
              <span className="kk-support-disposition-option__text">
                Further support needed
              </span>
            </label>
            {disposition === "resolved" ? (
              <p className="kk-support-disposition-case">
                Closed is final. The Client keeps the conversation history and
                opens a new Support Case for anything further.
              </p>
            ) : null}
            {disposition === "resolved" ? (
              <label className="kk-support-disposition-note">
                Closure note (optional)
                <textarea
                  rows={2}
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  disabled={ending}
                  placeholder="Short note for the Client history"
                />
              </label>
            ) : null}
            <div className="kk-support-disposition-actions">
              <button
                type="button"
                onClick={closeDispositionModal}
                disabled={ending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="kk-support-disposition-confirm"
                onClick={() => void endSession()}
                disabled={ending || !disposition}
              >
                {ending ? "Ending…" : "End Support Mode"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
